<script setup lang="ts">
// Toolkit Platform landing ("Concept A, flag on the planet"). On desktops the orbit is drawn on a stage in design
// units, scaled to fit its container and stretched to the container's aspect ratio (see platformLayout). On small
// screens, or by the user's choice, the tools are shown as a plain list instead. The planet and the orbit artwork are
// designer SVGs injected as markup so we can animate their parts and draw status badges and hover targets on top of
// them. The worker planting the flag is the user's own worker avatar, which they can restyle with "Suit up".
import { computed, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import { useDebounceFn, useElementSize, useLocalStorage, useSessionStorage } from '@vueuse/core';
import logoUrl from './toolkit-logo-on-dark.svg';
import dangerousPlanetSvg from './platform-planet.svg?raw';
import dangerousOrbitSvg from './platform-orbit.svg?raw';
import {
  platformStatusColors,
  platformStatusCounts,
  platformToolKeys,
  platformToolNeedsCount,
  platformStart,
  platformToolsBuild,
  platformToolsByState,
  platformDaysText,
  platformDueDateText,
  platformItemText,
  platformNextUp,
  platformFirstName,
  platformFlagText,
  PLATFORM_FLAG_TEXT_WIDTH,
  platformItemHref,
  platformToolHref,
  platformToolItems,
  platformToolState,
  platformToolStateColors,
  platformScopeFacilities,
  platformScopeResolve,
  type PlatformScopeParams,
  platformBoxTranslate,
  platformBoxUnion,
  platformEllipsePointToward,
  platformOrbitPlacements,
  PLATFORM_CENTRE_TOOL,
  type PlatformEllipse,
  platformLayout,
  PLATFORM_HEIGHT,
  PLATFORM_WIDE_WIDTH,
  type PlatformTools,
  type PlatformView,
} from './platform-landing';
import { facilitiesList } from '../facilities';
import type { AppID, FacilityID, TenantID } from '../../cross-app-shared/types/db';
import PlatformScopePicker from './PlatformScopePicker.vue';
import { you } from '../users';
import { dateLocal } from '../../cross-app-shared/services/dates-and-durations';
import type { PlatformToolStatusAggregated } from '../../cross-app-shared/types/platform-landing-types.ts';
import { crossAppActions } from '../cross-app-actions.ts';
import WorkerAvatar from '../worker-avatar/WorkerAvatar.vue';
import WorkerAvatarFace from '../worker-avatar/WorkerAvatarFace.vue';
import WorkerAvatarCustomizer from '../worker-avatar/WorkerAvatarCustomizer.vue';
import { WORKER_AVATAR_LOOK_DEFAULT } from '../worker-avatar/worker-avatar';
import type { WorkerAvatarLook } from '../../cross-app-shared/types/worker-avatar';

// Every app loads the facility list before mounting its pages, so the scope, and which tools the user can open, are
// known immediately. Only the statuses wait on the backend.
const props = defineProps<{
  // The tenant to show when there's no facility, or null for the user's primary tenant.
  tenantID: TenantID | null;
  // The facility to show, or to return to when showing all of its tenant's facilities.
  facilityID: FacilityID | null;
  // Whether to show all of the facility's tenant's facilities.
  all: boolean;
}>();

const emit = defineEmits<{
  scopeUpdate: [params: PlatformScopeParams];
}>();

const scope = computed(() =>
  platformScopeResolve(
    { tenantID: props.tenantID, facilityID: props.facilityID, all: props.all },
    facilitiesList.value,
    you.value.primaryTenantID,
  ),
);
const tenantName = computed(() => scope.value?.tenant.name ?? 'Toolkit');
// A userID of -1 means we failed to load the user.
const firstName = computed(() => (you.value.userID === -1 ? null : platformFirstName(you.value.name)));
// "Back" returns to the page the user came from, if it was in this app: vue-router records it in the history state, and
// Determinations, which isn't routed client-side, shows up as a same-origin referrer. Otherwise (e.g. a bookmark) it's a
// plain link to the app's home page, which every app serves at /.
const backIsHistory =
  window.history.state?.back != null ||
  (document.referrer !== '' && new URL(document.referrer).origin === window.location.origin);
function back(event: MouseEvent) {
  if (!backIsHistory) return;
  event.preventDefault();
  window.history.back();
}

// Statuses are kept per scope for the life of the page, so toggling between a facility and all facilities only fetches
// each once. Fetching them is expensive, as every app weighs in.
type StatusesFetch =
  | { kind: 'loading' }
  | { kind: 'loaded'; statuses: PlatformToolStatusAggregated }
  | { kind: 'failed' };
const statusesByScope = ref(new Map<string, StatusesFetch>());
const statusesArgs = computed(() => {
  if (!scope.value) return null;
  return scope.value.kind === 'facility'
    ? { facilityID: scope.value.facility.facilityID }
    : { tenantID: scope.value.tenant.tenantID };
});
watch(
  statusesArgs,
  async (args) => {
    if (!args) return;
    const key = JSON.stringify(args);
    if (statusesByScope.value.has(key)) return;
    statusesByScope.value.set(key, { kind: 'loading' });
    try {
      statusesByScope.value.set(key, { kind: 'loaded', statuses: await crossAppActions.platformStatus.list(args) });
    } catch (e) {
      console.error('Failed to load statuses:', e);
      statusesByScope.value.set(key, { kind: 'failed' });
    }
  },
  { immediate: true },
);
const statusesFetch = computed((): StatusesFetch | null =>
  statusesArgs.value === null
    ? null
    : (statusesByScope.value.get(JSON.stringify(statusesArgs.value)) ?? { kind: 'loading' }),
);
const statusesLoading = computed(() => statusesFetch.value?.kind === 'loading');
const statusesFailed = computed(() => statusesFetch.value?.kind === 'failed');
// Shown in place of a tool's statuses until they've loaded.
const pendingText = computed(() => (statusesLoading.value ? 'Checking status…' : `Couldn't load this tool's status.`));
const todayEyebrow = computed(() => (scope.value?.kind === 'facility' ? 'At this facility' : 'Across your facilities'));

// With no facilities, every tool is locked.
const tools = computed(
  (): PlatformTools =>
    platformToolsBuild(
      statusesFetch.value?.kind === 'loaded' ? statusesFetch.value.statuses : null,
      scope.value ? platformScopeFacilities(scope.value) : [],
    ),
);

const start = computed(() => platformStart(tools.value, scope.value));

const TAG_WIDTH = 300;
const SVG_NS = 'http://www.w3.org/2000/svg';

const rootEl = useTemplateRef<HTMLDivElement>('root');
const stageEl = useTemplateRef<HTMLDivElement>('stage');
const skyEl = useTemplateRef<HTMLDivElement>('sky');
const orbitEl = useTemplateRef<HTMLDivElement>('orbit');

const counts = computed(() => platformStatusCounts(tools.value));
const allClear = computed(() => counts.value.needs === 0 && counts.value.soon === 0);
const today = dateLocal();
const nextUp = computed(() => platformNextUp(tools.value, today));
const nextUpHref = computed(() =>
  nextUp.value
    ? (platformItemHref(nextUp.value.item, scope.value) ?? platformToolHref(nextUp.value.tool, scope.value))
    : null,
);
const toolsByState = computed(() => platformToolsByState(tools.value));

const viewPreference = useLocalStorage<PlatformView>('platform-landing-view', 'orbit');
const { width: rootWidth, height: rootHeight } = useElementSize(rootEl);
// Nothing renders until the container has been measured, so we never flash the wrong layout.
const layout = computed(() =>
  rootWidth.value > 0 ? platformLayout(rootWidth.value, rootHeight.value, viewPreference.value) : null,
);
const orbitLayout = computed(() => (layout.value?.kind === 'orbit' ? layout.value : null));
const listLayout = computed(() => (layout.value?.kind === 'list' ? layout.value : null));

interface Star {
  x: number;
  y: number;
  r: number;
  fill: string;
  opacity: number;
  twinkleDelay: number | null;
}

// Stars, seeded so every load draws the same sky. The sky is tiled in design-sized tiles from the bottom left, where
// the planet is anchored, so resizing the window reveals more sky rather than moving the stars around.
const stars = computed((): Star[] => {
  const stage = orbitLayout.value;
  if (!stage) return [];
  const out: Star[] = [];
  const tilesAcross = Math.ceil(stage.stageWidth / PLATFORM_WIDE_WIDTH);
  const tilesUp = Math.ceil(stage.stageHeight / PLATFORM_HEIGHT);
  const planetX = 360;
  const planetY = stage.stageHeight - PLATFORM_HEIGHT + 1585;
  for (let across = 0; across < tilesAcross; across++) {
    for (let up = 0; up < tilesUp; up++) {
      let s = 11 + across * 7919 + up * 104729;
      const rnd = () => {
        s = (s * 16807) % 2147483647;
        return (s - 1) / 2147483646;
      };
      for (let i = 0; i < 150; i++) {
        const x = across * PLATFORM_WIDE_WIDTH + rnd() * PLATFORM_WIDE_WIDTH;
        const y = stage.stageHeight - (up + 1) * PLATFORM_HEIGHT + rnd() * PLATFORM_HEIGHT;
        const r = 0.8 + rnd() * 1.9;
        const opacity = 0.25 + rnd() * 0.6;
        if (x > stage.stageWidth || y < 0) continue;
        if (stage.wide && Math.hypot(x - planetX, y - planetY) < 812) continue; // not on the planet
        const fill = rnd() < 0.12 ? '#0FA96A' : '#FFFFFF';
        const twinkleDelay = rnd() < 0.18 ? rnd() * 4 : null;
        out.push({ x, y, r, fill, opacity, twinkleDelay });
      }
    }
  }
  return out;
});

// Hover tag over the orbit, positioned in stage coordinates. It overlaps its tool and lingers briefly after the pointer
// leaves, so the pointer can move onto it to follow its statuses' links.
const tag = ref<{ key: AppID; x: number; y: number } | null>(null);
const hitTargets = new Map<AppID, SVGElement>();
const TAG_HIDE_DELAY_MS = 250;
let tagHideTimeout: ReturnType<typeof setTimeout> | null = null;

function orbitSvg(): SVGSVGElement | null {
  return orbitEl.value?.querySelector('svg') ?? null;
}

function tagShow(key: AppID) {
  tagHideCancel();
  const hit = hitTargets.get(key);
  const svg = orbitSvg();
  const stage = orbitLayout.value;
  if (!hit || !svg || !stageEl.value || !stage) return;
  const r = hit.getBoundingClientRect();
  const sr = stageEl.value.getBoundingClientRect();
  let x = (r.right - sr.left) / stage.scale - 10;
  const y = (r.top - sr.top) / stage.scale + 10;
  if (x + TAG_WIDTH > stage.stageWidth - 20) x = (r.left - sr.left) / stage.scale - TAG_WIDTH + 10;
  tag.value = { key, x, y };
  svg.classList.add('dim');
  svg.querySelectorAll('.pf-tool').forEach((el) => el.classList.toggle('hot', el.classList.contains(`pf-${key}`)));
}

function tagHide() {
  tagHideCancel();
  tag.value = null;
  orbitSvg()?.classList.remove('dim');
}

function tagHideSoon() {
  tagHideCancel();
  tagHideTimeout = setTimeout(tagHide, TAG_HIDE_DELAY_MS);
}

function tagHideCancel() {
  if (tagHideTimeout) clearTimeout(tagHideTimeout);
  tagHideTimeout = null;
}

// Lays out the orbit and draws statuses onto it. Tools are moved into place around the rings (see
// platformOrbitPlacements), each gets a status dot coloured by its state, "needs you" adds a numbered badge in place of
// the dot, and an invisible rect over each tool drives the hover tag and links to the app.
function orbitDecorate(svg: SVGSVGElement, tools: PlatformTools, loading: boolean) {
  svg.querySelector('#pf-overlay')?.remove();
  hitTargets.clear();
  const layer = document.createElementNS(SVG_NS, 'g');
  layer.setAttribute('id', 'pf-overlay');
  svg.appendChild(layer);

  // Behind each tool, #Knockouts holds navy shapes that blank out the orbit rings: the label's outline, named
  // `<prefix>_label_outline`, followed by an unnamed shape behind the icon. They move and hide along with the tool.
  const knockouts = (prefix: string): SVGGraphicsElement[] => {
    const outline = svg.querySelector<SVGGraphicsElement>(`#Knockouts > #${prefix}_label_outline`);
    const behindIcon = outline?.nextElementSibling;
    return [outline, behindIcon && !behindIcon.id ? (behindIcon as SVGGraphicsElement) : null].filter((k) => !!k);
  };

  // Artwork for tools that aren't configured (e.g. Consulting) stays in the file but isn't shown.
  const svgPrefixes = new Set(platformToolKeys.map((key) => tools[key].svgPrefix));
  for (const group of svg.querySelectorAll<SVGGElement>('[data-tool]')) {
    const prefix = group.dataset.tool ?? '';
    if (svgPrefixes.has(prefix)) continue;
    for (const el of [group, ...knockouts(prefix)]) el.style.display = 'none';
  }

  // Each tool's artwork: its icon and label, which the layout measures, and its knockouts, which just move with them.
  const parts = new Map<AppID, { measured: SVGGraphicsElement[]; all: SVGGraphicsElement[] }>();
  for (const key of platformToolKeys) {
    const { svgPrefix } = tools[key];
    const icon = svg.querySelector<SVGGraphicsElement>(`[id^="${svgPrefix}_icon"]`);
    const label = svg.querySelector<SVGGraphicsElement>(`[data-tool] [id^="${svgPrefix}_label"]`);
    if (!icon || !label) continue;
    parts.set(key, { measured: [icon, label], all: [icon, label, ...knockouts(svgPrefix)] });
  }
  const partsBox = (key: AppID) => platformBoxUnion(parts.get(key)!.measured.map((p) => p.getBBox()));

  const rings: PlatformEllipse[] = Array.from(svg.querySelectorAll<SVGEllipseElement>('#Orbit_rings ellipse'))
    .map((e) => ({ cx: e.cx.baseVal.value, cy: e.cy.baseVal.value, rx: e.rx.baseVal.value, ry: e.ry.baseVal.value }))
    .sort((a, b) => a.rx - b.rx);
  const ringKeys = platformToolKeys.filter((key) => key !== PLATFORM_CENTRE_TOOL && parts.has(key));
  const placements = platformOrbitPlacements(
    ringKeys.map((key) => ({ unit: partsBox(key), icon: parts.get(key)!.measured[0].getBBox() })),
    rings,
    parts.has(PLATFORM_CENTRE_TOOL) ? [partsBox(PLATFORM_CENTRE_TOOL)] : [],
  );
  const placementByKey = new Map(ringKeys.map((key, i) => [key, placements[i]]));

  platformToolKeys.forEach((key, i) => {
    const toolParts = parts.get(key);
    if (!toolParts) return;
    const tool = tools[key];
    const state = platformToolState(tool);
    const placement = placementByKey.get(key) ?? null;
    const dx = placement?.dx ?? 0;
    const dy = placement?.dy ?? 0;
    const b = platformBoxTranslate(partsBox(key), dx, dy);
    const ib = platformBoxTranslate(toolParts.measured[0].getBBox(), dx, dy);

    for (const p of toolParts.all) {
      p.classList.add('pf-tool', `pf-${key}`);
      // Knockouts stay navy, so they keep hiding the rings.
      p.classList.toggle('pf-locked', state === 'locked' && !p.closest('#Knockouts'));
      p.style.setProperty('--i', String(i));
      // Moved by a wrapping group, as each part's own transform is used by its pop-in animation. Redecorating reuses
      // the wrapper from the last pass rather than stacking another translation on top of it.
      const existing = p.parentElement?.classList.contains('pf-shift') ? p.parentElement : null;
      if (existing) {
        existing.setAttribute('transform', `translate(${dx},${dy})`);
      } else if (placement) {
        const wrapper = document.createElementNS(SVG_NS, 'g');
        wrapper.setAttribute('class', 'pf-shift');
        wrapper.setAttribute('transform', `translate(${dx},${dy})`);
        p.replaceWith(wrapper);
        wrapper.appendChild(p);
      }
    }

    // The centre tool's dot goes on the innermost ring, at the point nearest its icon's top-right corner.
    const { x: cx, y: cy } = placement?.dot ?? platformEllipsePointToward(rings[0], ib.x + ib.width, ib.y);
    if (state === 'needs') {
      const badge = document.createElementNS(SVG_NS, 'g');
      badge.setAttribute('class', 'pf-badge');
      badge.style.setProperty('--i', String(i));
      badge.innerHTML =
        `<circle class="pf-pulse" cx="${cx}" cy="${cy}" r="50"/>` +
        `<circle cx="${cx}" cy="${cy}" r="52" fill="#FF5D1D" stroke="#081848" stroke-width="11"/>` +
        `<text x="${cx}" y="${cy + 18.3}" text-anchor="middle" font-weight="700" font-size="58" fill="#081848"></text>`;
      badge.querySelector('text')!.textContent = String(platformToolNeedsCount(tool));
      layer.appendChild(badge);
    } else {
      // Drawn like the designer's dots: a solid centre inside a faint ring.
      const color = platformToolStateColors[state];
      const dot = document.createElementNS(SVG_NS, 'g');
      dot.setAttribute('class', 'pf-node');
      dot.style.setProperty('--i', String(i));
      // Pending dots breathe while their statuses load.
      const loadingClass = state === 'pending' && loading ? ' class="pl-loading"' : '';
      dot.innerHTML =
        `<circle cx="${cx}" cy="${cy}" r="36" fill="none" stroke="${color}" stroke-width="6" opacity="0.31"/>` +
        `<circle cx="${cx}" cy="${cy}" r="21.5" fill="${color}"${loadingClass}/>`;
      layer.appendChild(dot);
    }

    const hit = document.createElementNS(SVG_NS, 'rect');
    hit.setAttribute('class', 'pf-hit');
    hit.setAttribute('x', String(b.x - 30));
    hit.setAttribute('y', String(b.y - 30));
    hit.setAttribute('width', String(b.width + 60));
    hit.setAttribute('height', String(b.height + 60));

    // Apps the tenant can access are links; anything else is still focusable so keyboard users can read its tag.
    const href = platformToolHref(tool, scope.value);
    let target: SVGElement = hit;
    if (href) {
      target = document.createElementNS(SVG_NS, 'a');
      target.setAttribute('href', href);
      target.appendChild(hit);
    } else {
      hit.setAttribute('tabindex', '0');
      if (state === 'locked') hit.classList.add('pf-hit-locked');
    }
    target.setAttribute('aria-label', tool.name);
    target.addEventListener('mouseenter', () => tagShow(key));
    target.addEventListener('focus', () => tagShow(key));
    target.addEventListener('mouseleave', tagHideSoon);
    target.addEventListener('blur', tagHide);
    layer.appendChild(target);
    hitTargets.set(key, target);
  });
}

// The look is saved on the user, so it follows them between devices. Changes show at once; saving waits for the user
// to pause, so dragging through the colour picker doesn't send a request per step.
const workerLookSave = useDebounceFn((avatar: WorkerAvatarLook) => {
  crossAppActions.users.youAvatarUpdate({ avatar }).catch((e) => console.error('Failed to save avatar', e));
}, 600);
const workerLook = computed<WorkerAvatarLook>({
  get: () => you.value.avatar ?? WORKER_AVATAR_LOOK_DEFAULT,
  set: (avatar) => {
    you.value = { ...you.value, avatar };
    // A userID of -1 means we failed to load the user, so there's no one to save it on.
    if (you.value.userID !== -1) workerLookSave(avatar);
  },
});
const customizerOpen = ref(false);

// The flag's text is fitted to the cloth by measuring candidate layouts in place. The designer's text is 34px with
// 2.5px letter spacing, centred at x=106, with its baseline at y=62.
const FLAG_TEXT_CENTER_X = 106;
const FLAG_TEXT_CENTER_Y = 50;
function flagTextUpdate() {
  const text = skyEl.value?.querySelector<SVGTextElement>('#flagcloth text');
  if (!text) return;
  const textStyle = (fontSize: number, tracked: boolean) => {
    text.setAttribute('font-size', String(fontSize));
    text.setAttribute('letter-spacing', tracked ? String((2.5 * fontSize) / 34) : '0');
  };
  text.removeAttribute('y');
  const flag = platformFlagText(tenantName.value, (line, fontSize, tracked) => {
    textStyle(fontSize, tracked);
    text.textContent = line;
    return text.getBBox().width;
  });

  textStyle(flag.fontSize, flag.tracked);
  text.textContent = '';
  // Capitals sit about 0.72em above the baseline; lines are spaced 1em apart and centred as a block.
  const firstBaseline = FLAG_TEXT_CENTER_Y + (0.72 * flag.fontSize) / 2 - ((flag.lines.length - 1) * flag.fontSize) / 2;
  flag.lines.forEach((line, i) => {
    const tspan = document.createElementNS(SVG_NS, 'tspan');
    tspan.setAttribute('x', String(FLAG_TEXT_CENTER_X));
    tspan.setAttribute('y', String(firstBaseline + i * flag.fontSize));
    if (line.squeeze) {
      tspan.setAttribute('textLength', String(PLATFORM_FLAG_TEXT_WIDTH));
      tspan.setAttribute('lengthAdjust', 'spacingAndGlyphs');
    }
    tspan.textContent = line.text;
    text.appendChild(tspan);
  });
}

// The storyline is driven by CSS animations under `.play`. It plays once per browser session; after that the page is
// mostly used as an app switcher, so it appears immediately.
const animationPlayed = useSessionStorage('platform-landing-animated', false);
const animate = !animationPlayed.value;
animationPlayed.value = true;

// The orbit and the planet come and go as the layout changes, so set them up whenever they appear. The orbit is also
// redecorated whenever the tools' statuses change, e.g. once they've been fetched.
watch(
  [orbitEl, tools, scope, statusesLoading],
  () => {
    const svg = orbitSvg();
    if (svg) orbitDecorate(svg, tools.value, statusesLoading.value);
  },
  { flush: 'post' },
);
watch(
  skyEl,
  (sky) => {
    if (!sky) return;
    flagTextUpdate();
    // Widths change once the web font arrives.
    void document.fonts.ready.then(flagTextUpdate);
  },
  { flush: 'post' },
);
watch(tenantName, flagTextUpdate);
watch(
  () => [layout.value?.kind, orbitLayout.value?.wide],
  () => tagHide(),
);

onBeforeUnmount(() => {
  tagHideCancel();
});
</script>

<template>
  <div ref="root" class="platform-landing">
    <div
      v-if="orbitLayout"
      ref="stage"
      class="pl-stage"
      :class="{ play: animate }"
      :style="{
        width: `${orbitLayout.stageWidth}px`,
        height: `${orbitLayout.stageHeight}px`,
        transform: `scale(${orbitLayout.scale})`,
      }"
    >
      <svg class="pl-stars" :viewBox="`0 0 ${orbitLayout.stageWidth} ${orbitLayout.stageHeight}`" aria-hidden="true">
        <circle
          v-for="(star, i) in stars"
          :key="i"
          :cx="star.x"
          :cy="star.y"
          :r="star.r"
          :fill="star.fill"
          :opacity="star.opacity"
          :class="{ twk: star.twinkleDelay !== null }"
          :style="star.twinkleDelay !== null ? { animationDelay: `${star.twinkleDelay}s` } : undefined"
        />
      </svg>
      <div v-if="orbitLayout.wide" ref="sky" class="pl-planet">
        <div class="pl-planet-ground" v-html="dangerousPlanetSvg"></div>
        <svg viewBox="0 0 1600 900" aria-hidden="true">
          <defs>
            <clipPath id="flagshape"><path d="M0,0 C55,-10 120,12 200,0 L200,116 C120,128 55,106 0,116 Z" /></clipPath>
          </defs>
          <!-- The worker plants the flag: they reach up with it furled and drive the pole 30px into the ground, dust kicks
               up, then it unfurls. Mirrored so the flag flies toward the orbit. -->
          <g id="man" class="pl-worker" @click="customizerOpen = true">
            <ellipse cx="352" cy="793" rx="64" ry="7" fill="#06171B" opacity=".6" />
            <ellipse class="pl-hole" cx="430.6" cy="797" rx="10" ry="2.6" fill="#06171B" opacity=".7" />
            <g transform="translate(352,792) scale(-0.52,0.52) translate(-174,-626)">
              <WorkerAvatar :look="workerLook" pose="holdingPole" />
            </g>
            <g class="pl-plant">
              <g transform="translate(433,472.6) scale(.72)">
                <g id="flagcloth">
                  <g clip-path="url(#flagshape)">
                    <path d="M0,0 C55,-10 120,12 200,0 L200,116 C120,128 55,106 0,116 Z" fill="#FFFFFF" />
                    <path d="M0,94 C55,84 120,106 200,94 L200,116 C120,128 55,106 0,116 Z" fill="#0FA96A" />
                    <rect x="0" y="-12" width="12" height="140" fill="#E2E7E8" />
                  </g>
                  <text
                    text-anchor="middle"
                    font-family="Adobe Clean"
                    font-weight="800"
                    font-size="34"
                    letter-spacing="2.5"
                    fill="#081848"
                    transform="skewY(1.5)"
                  ></text>
                </g>
              </g>
              <circle cx="430.6" cy="467.6" r="5" fill="#FFC400" />
            </g>
            <g class="dust" transform="translate(430.6,797)">
              <circle cx="-8" cy="-3" r="6" fill="#92A5B9" style="--dx: -18px" />
              <circle cx="8" cy="-4" r="5.5" fill="#92A5B9" style="--dx: 16px" />
              <circle cx="-2" cy="-7" r="4" fill="#E2E7E8" style="--dx: -6px" />
              <circle cx="14" cy="-2" r="3.5" fill="#E2E7E8" style="--dx: 24px" />
            </g>
          </g>
        </svg>
      </div>
      <button
        v-if="orbitLayout.wide"
        class="pl-suit-up"
        type="button"
        :aria-expanded="customizerOpen"
        @click="customizerOpen = !customizerOpen"
      >
        <WorkerAvatarFace class="pl-suit-up-face" :look="workerLook" />Suit up
      </button>

      <header class="pl-header">
        <div class="pl-brand">
          <img :src="logoUrl" alt="Toolkit" />
          <div class="pl-eyebrow">Mission control</div>
        </div>
        <div class="pl-acct">
          <a class="pl-back" href="/" @click="back">&larr; Back</a>
          <div class="pl-toggle" role="group" aria-label="View">
            <button type="button" aria-pressed="true">Orbit</button>
            <button type="button" aria-pressed="false" @click="viewPreference = 'list'">List</button>
          </div>
          <PlatformScopePicker
            v-if="scope"
            :scope
            :facilities="facilitiesList"
            @scopeUpdate="emit('scopeUpdate', $event)"
          />
        </div>
      </header>

      <div class="pl-frame" :style="{ top: `${(orbitLayout.stageHeight - PLATFORM_HEIGHT) / 2}px` }">
        <section v-if="orbitLayout.wide" class="pl-copy">
          <h1>
            Welcome back<template v-if="firstName">,<br />{{ firstName }}</template
            >.
          </h1>
          <component :is="nextUpHref ? 'a' : 'div'" v-if="nextUp" class="pl-next" :href="nextUpHref ?? undefined">
            <div class="pl-next-label">Next up</div>
            <div class="pl-next-title">
              <i :style="{ background: platformStatusColors[nextUp.item.status] }"></i>{{ nextUp.item.text }}
            </div>
            <div class="pl-next-meta">
              {{ nextUp.tool.name
              }}<template v-if="nextUp.due">
                · {{ platformDueDateText(nextUp.due.date, today) }} · {{ platformDaysText(nextUp.due.days) }}</template
              >
            </div>
          </component>
          <div class="pl-key">
            <span><i class="pl-needs"></i>Needs you</span>
            <span><i class="pl-soon"></i>Coming up</span>
            <span><i class="pl-clear"></i>All clear</span>
          </div>
          <p class="pl-hint">Hover a tool for what's due, click to open it.</p>
        </section>

        <div class="pl-main" :class="orbitLayout.wide ? 'pl-main-wide' : 'pl-main-narrow'">
          <div ref="orbit" class="pl-orbit" v-html="dangerousOrbitSvg"></div>

          <section class="pl-today">
            <div class="pl-eyebrow">{{ todayEyebrow }}</div>
            <div v-if="statusesLoading" class="pl-count pl-count-clear">
              <i class="pl-pending pl-loading"></i><b>Checking your tools…</b>
            </div>
            <div v-else-if="statusesFailed" class="pl-count pl-count-clear">
              <i class="pl-pending"></i><b>Couldn't load statuses</b>your tools are still a click away
            </div>
            <div v-else-if="allClear" class="pl-count pl-count-clear">
              <i class="pl-clear"></i><b>All clear</b>nothing needs you or is coming up
            </div>
            <template v-else>
              <div class="pl-count">
                <i class="pl-needs"></i><b>{{ counts.needs }}</b
                >need you
              </div>
              <div class="pl-count">
                <i class="pl-soon"></i><b>{{ counts.soon }}</b
                >coming up
              </div>
              <a v-if="start" class="pl-go" :href="start.href"
                >Start with {{ start.status === 'needs' ? 'what needs you' : "what's coming up" }} &rarr;</a
              >
            </template>
          </section>
        </div>

        <Transition name="pl-customizer">
          <!-- Right of the flag, over the orbit, so the worker stays in full view. -->
          <WorkerAvatarCustomizer
            v-if="customizerOpen && orbitLayout.wide"
            v-model:look="workerLook"
            class="pl-customizer"
            :company="null"
            @close="customizerOpen = false"
          />
        </Transition>
      </div>

      <Transition name="pl-tag">
        <div
          v-if="tag"
          class="pl-tag"
          role="status"
          :style="{ left: `${tag.x}px`, top: `${tag.y}px` }"
          @mouseenter="tagHideCancel"
          @mouseleave="tagHideSoon"
        >
          <b>{{ tools[tag.key].name }}</b>
          <div v-if="!tools[tag.key].access.granted">
            <i :style="{ background: platformToolStateColors.locked }"></i><span>Contact us for access.</span>
          </div>
          <div v-else-if="platformToolState(tools[tag.key]) === 'pending'">
            <i :style="{ background: platformToolStateColors.pending }"></i><span>{{ pendingText }}</span>
          </div>
          <div v-for="(item, i) in platformToolItems(tools[tag.key])" :key="i">
            <i :style="{ background: platformStatusColors[item.status] }"></i
            ><component
              :is="platformItemHref(item, scope) ? 'a' : 'span'"
              :href="platformItemHref(item, scope) ?? undefined"
              >{{ platformItemText(item, today) }}</component
            >
          </div>
        </div>
      </Transition>
    </div>

    <!-- The list scrolls inside the root, never the root itself: a scrollbar on the root would resize it and re-trigger
         the size observer that picks the layout. -->
    <div v-else-if="listLayout" class="pl-list">
      <div class="pl-list-content">
        <header class="pl-list-header">
          <div class="pl-brand">
            <img :src="logoUrl" alt="Toolkit" />
            <div class="pl-eyebrow">Mission control</div>
          </div>
          <div class="pl-acct">
            <a class="pl-back" href="/" @click="back">&larr; Back</a>
            <div v-if="listLayout.toggleable" class="pl-toggle" role="group" aria-label="View">
              <button type="button" aria-pressed="false" @click="viewPreference = 'orbit'">Orbit</button>
              <button type="button" aria-pressed="true">List</button>
            </div>
          </div>
        </header>

        <h1>
          Welcome back<template v-if="firstName">, {{ firstName }}</template
          >.
        </h1>
        <PlatformScopePicker
          v-if="scope"
          :scope
          :facilities="facilitiesList"
          @scopeUpdate="emit('scopeUpdate', $event)"
        />

        <component :is="nextUpHref ? 'a' : 'div'" v-if="nextUp" class="pl-next" :href="nextUpHref ?? undefined">
          <div class="pl-next-label">Next up</div>
          <div class="pl-next-title">
            <i :style="{ background: platformStatusColors[nextUp.item.status] }"></i>{{ nextUp.item.text }}
          </div>
          <div class="pl-next-meta">
            {{ nextUp.tool.name
            }}<template v-if="nextUp.due">
              · {{ platformDueDateText(nextUp.due.date, today) }} · {{ platformDaysText(nextUp.due.days) }}</template
            >
          </div>
        </component>

        <section class="pl-list-today">
          <div class="pl-eyebrow">{{ todayEyebrow }}</div>
          <div class="pl-list-counts">
            <div v-if="statusesLoading" class="pl-count pl-count-clear">
              <i class="pl-pending pl-loading"></i><b>Checking your tools…</b>
            </div>
            <div v-else-if="statusesFailed" class="pl-count pl-count-clear">
              <i class="pl-pending"></i><b>Couldn't load statuses</b>
            </div>
            <div v-else-if="allClear" class="pl-count pl-count-clear">
              <i class="pl-clear"></i><b>All clear</b>nothing needs you or is coming up
            </div>
            <template v-else>
              <div class="pl-count">
                <i class="pl-needs"></i><b>{{ counts.needs }}</b
                >need you
              </div>
              <div class="pl-count">
                <i class="pl-soon"></i><b>{{ counts.soon }}</b
                >coming up
              </div>
            </template>
          </div>
        </section>

        <div class="pl-key">
          <span><i class="pl-needs"></i>Needs you</span>
          <span><i class="pl-soon"></i>Coming up</span>
          <span><i class="pl-clear"></i>All clear</span>
        </div>

        <ul class="pl-list-tools">
          <li v-for="{ key, tool, state } in toolsByState" :key="key">
            <!-- The tool's name links to it across the whole row, with its statuses' own links on top. -->
            <div class="pl-list-tool" :class="{ 'pl-list-tool-locked': state === 'locked' }">
              <span class="pl-list-icon">
                <img :src="tool.iconUrl" alt="" />
                <span
                  class="pl-list-dot"
                  :class="{
                    'pl-list-dot-count': state === 'needs',
                    'pl-loading': state === 'pending' && statusesLoading,
                  }"
                  :style="{ background: platformToolStateColors[state] }"
                >
                  <template v-if="state === 'needs'">{{ platformToolNeedsCount(tool) }}</template>
                </span>
              </span>
              <span class="pl-list-body">
                <a v-if="platformToolHref(tool, scope)" class="pl-list-tool-link" :href="platformToolHref(tool, scope)!"
                  ><b>{{ tool.name }}</b></a
                >
                <b v-else>{{ tool.name }}</b>
                <span v-if="state === 'locked'" class="pl-list-item">Contact us for access.</span>
                <span v-else-if="state === 'pending'" class="pl-list-item">{{ pendingText }}</span>
                <component
                  :is="platformItemHref(item, scope) ? 'a' : 'span'"
                  v-for="(item, i) in platformToolItems(tool)"
                  :key="i"
                  class="pl-list-item"
                  :href="platformItemHref(item, scope) ?? undefined"
                >
                  <i :style="{ background: platformStatusColors[item.status] }"></i>{{ platformItemText(item, today) }}
                </component>
              </span>
              <span v-if="platformToolHref(tool, scope)" class="pl-list-arrow" aria-hidden="true">&rarr;</span>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style>
/* Unscoped because most of these rules reach into the injected SVG markup; everything is prefixed with
   .platform-landing instead. Palette: navy, white, yellow, green, orange, plus the icon greys, which are used for
   hardware and shading only: no type ever sits on grey. */
.platform-landing {
  --pl-navy: #081848;
  --pl-white: #ffffff;
  --pl-yellow: #ffc400;
  --pl-green: #0fa96a;
  --pl-orange: #ff5d1d;
  /* Light enough to read as a link against the navy. */
  --pl-link: #8ccbff;
  --pl-link-on-white: #1a5fd0;
  --pl-g3: #2d475e;
  --pl-g4: #92a5b9;

  position: relative;
  overflow: hidden;
  background: var(--pl-navy);
  font-family: 'Adobe Clean', system-ui, sans-serif;
  font-weight: 300;
  color: var(--pl-white);
}
.platform-landing *,
.platform-landing *::before,
.platform-landing *::after {
  box-sizing: border-box;
}
.platform-landing text {
  font-family: 'Adobe Clean', system-ui, sans-serif;
}

/* Sized and scaled from platformLayout so that it always fills the container exactly. */
.platform-landing .pl-stage {
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: 0 0;
  overflow: hidden;
}
.platform-landing .pl-stars {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
/* A design-height band, vertically centred, holding everything but the header, sky and planet. */
.platform-landing .pl-frame {
  position: absolute;
  left: 0;
  right: 0;
  height: 900px;
}
.platform-landing .pl-main {
  position: absolute;
  top: 96px;
  bottom: 76px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.platform-landing .pl-main-wide {
  left: 556px;
  right: 24px;
}
.platform-landing .pl-main-narrow {
  left: 72px;
  right: 72px;
}

.platform-landing .pl-eyebrow {
  font-weight: 700;
  font-size: 15px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--pl-yellow);
}
.platform-landing h1 {
  font-weight: 700;
  margin: 0;
  line-height: 1.02;
  letter-spacing: -0.01em;
}
.platform-landing p {
  margin: 0;
}

/* Status colours. Orange = needs you. */
.platform-landing .pl-key {
  display: flex;
  gap: 18px;
  font-size: 15px;
  font-weight: 400;
}
.platform-landing .pl-key span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}
.platform-landing .pl-key i {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.platform-landing .pl-needs {
  background: var(--pl-orange);
}
.platform-landing .pl-soon {
  background: var(--pl-yellow);
}
.platform-landing .pl-clear {
  background: var(--pl-green);
}
.platform-landing .pl-pending {
  background: var(--pl-g4);
}
/* Statuses on their way. Used on HTML dots and on the SVG dots drawn onto the orbit. */
.platform-landing .pl-loading {
  animation: pl-breathe 1.4s ease-in-out infinite;
}

/* Hover tag over the orbit */
.platform-landing .pl-tag {
  position: absolute;
  z-index: 30;
  background: var(--pl-white);
  color: var(--pl-navy);
  border-radius: 14px;
  padding: 12px 16px 13px;
  min-width: 230px;
  max-width: 300px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}
.platform-landing .pl-tag b {
  display: block;
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 4px;
}
.platform-landing .pl-tag div {
  font-size: 15px;
  font-weight: 400;
  line-height: 1.3;
  display: flex;
  gap: 8px;
  align-items: baseline;
}
.platform-landing .pl-tag div + div {
  margin-top: 4px;
}
.platform-landing .pl-tag a {
  color: var(--pl-link-on-white);
  text-decoration: underline;
  text-underline-offset: 3px;
}
.platform-landing .pl-tag a:hover {
  color: var(--pl-navy);
}
.platform-landing .pl-tag i {
  flex: none;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  transform: translateY(-1px);
}
.pl-tag-enter-active,
.pl-tag-leave-active {
  transition:
    opacity 0.18s,
    transform 0.18s;
}
/* A tag on its way out can't be caught and kept. */
.pl-tag-leave-active {
  pointer-events: none;
}
.pl-tag-enter-from,
.pl-tag-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* Statuses drawn onto the orbit artwork. The designer's dots sit at fixed positions, so they're hidden and each tool's
   dot is drawn beside wherever the tool ends up. */
.platform-landing #Nodes {
  display: none;
}
.platform-landing .pf-node,
.platform-landing .pf-badge {
  pointer-events: none;
}
.platform-landing .pf-pulse {
  fill: none;
  stroke: #ff5d1d;
  stroke-width: 6;
  opacity: 0;
  transform-box: fill-box;
  transform-origin: center;
  animation: pl-pulse 2.6s ease-out infinite;
}
.platform-landing .pf-hit {
  cursor: pointer;
  fill: transparent;
}
.platform-landing .pf-tool {
  transition: opacity 0.25s;
}
/* Tools the tenant has no access to are greyed out, but still brighten a little on hover to show their tag. */
.platform-landing .pf-tool.pf-locked {
  filter: grayscale(1);
  opacity: 0.35;
}
.platform-landing .dim .pf-tool.pf-locked.hot {
  opacity: 0.6;
}
.platform-landing .pf-hit-locked {
  cursor: default;
}
.platform-landing .dim .pf-tool:not(.hot) {
  opacity: 0.35;
}

/* ---- the flag on the planet ---- */
/* Anchored bottom-left, at design size. */
.platform-landing .pl-planet {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 1600px;
  height: 900px;
  pointer-events: none;
}
.platform-landing .pl-planet svg,
.platform-landing .pl-planet-ground {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}
.platform-landing .pl-worker {
  pointer-events: auto;
  cursor: pointer;
}
/* The frame spans the stage, so it lets clicks through to the worker beneath it. */
.platform-landing .pl-frame {
  pointer-events: none;
}
.platform-landing .pl-frame > * {
  pointer-events: auto;
}

.platform-landing .pl-suit-up {
  position: absolute;
  left: 72px;
  bottom: 30px;
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 9px;
  background: rgba(6, 23, 27, 0.55);
  border: 1.5px solid var(--pl-yellow);
  border-radius: 999px;
  color: var(--pl-yellow);
  font: inherit;
  font-weight: 700;
  font-size: 15px;
  padding: 5px 17px 5px 5px;
  cursor: pointer;
}
.platform-landing .pl-suit-up:hover {
  background: rgba(255, 196, 0, 0.12);
}
.platform-landing .pl-suit-up-face {
  width: 30px;
  height: 30px;
  display: block;
}
.platform-landing .pl-customizer {
  position: absolute;
  left: 600px;
  top: 106px;
  z-index: 8;
}
.pl-customizer-enter-active,
.pl-customizer-leave-active {
  transition:
    opacity 0.25s,
    transform 0.25s;
}
.pl-customizer-enter-from,
.pl-customizer-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
.platform-landing .twk {
  animation: pl-twinkle 3.2s ease-in-out infinite;
}

.platform-landing .pl-header {
  position: absolute;
  left: 72px;
  right: 72px;
  top: 38px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 5;
}
.platform-landing .pl-brand {
  display: flex;
  align-items: center;
  gap: 24px;
}
.platform-landing .pl-header img {
  height: 58px;
  display: block;
  margin-left: -10px;
}
.platform-landing .pl-acct {
  display: flex;
  align-items: center;
  gap: 22px;
  font-size: 16px;
  font-weight: 400;
}
.platform-landing .pl-pill {
  background: #fff;
  color: var(--pl-navy);
  border-radius: 10px;
  padding: 9px 14px;
  font-weight: 700;
}
.platform-landing .pl-pill-picker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 0;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
.platform-landing .pl-pill-picker i {
  font-size: 0.75em;
  opacity: 0.6;
}
.platform-landing .pl-pill-text {
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.platform-landing .pl-scope {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  min-width: 0;
}
.platform-landing .pl-back {
  color: var(--pl-white);
  text-decoration: none;
  white-space: nowrap;
}
.platform-landing .pl-back:hover,
.platform-landing .pl-back:focus-visible {
  text-decoration: underline;
}
.platform-landing .pl-list-header .pl-acct {
  gap: 16px;
  font-size: 15px;
}
.platform-landing .pl-toggle {
  display: inline-flex;
  border: 1.5px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  padding: 3px;
}
.platform-landing .pl-toggle button {
  color: #fff;
  background: none;
  border: 0;
  border-radius: 999px;
  padding: 6px 14px;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}
.platform-landing .pl-toggle button[aria-pressed='true'] {
  background: #fff;
  color: var(--pl-navy);
  font-weight: 700;
  cursor: default;
}

.platform-landing .pl-copy {
  position: absolute;
  left: 72px;
  top: 132px;
  width: 470px;
  z-index: 5;
}
.platform-landing .pl-copy h1 {
  font-size: 60px;
  margin: 0 0 20px;
}
.platform-landing .pl-copy p {
  font-size: 20px;
  line-height: 1.42;
}
.platform-landing .pl-next {
  display: block;
  max-width: 320px; /* Clear of the flagpole. */
  padding: 12px 16px;
  border: 1.5px solid var(--pl-g3);
  border-radius: 16px;
  color: inherit;
  text-decoration: none;
}
.platform-landing a.pl-next:hover,
.platform-landing a.pl-next:focus-visible {
  border-color: var(--pl-g4);
}
.platform-landing .pl-next-label {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--pl-g4);
}
.platform-landing .pl-next-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
  font-size: 20px;
  font-weight: 700;
}
.platform-landing .pl-next-title i {
  flex: none;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.platform-landing .pl-next-meta {
  margin-top: 4px;
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
}
.platform-landing .pl-list .pl-next {
  max-width: none;
  margin-top: 20px;
}
.platform-landing .pl-copy .pl-key {
  margin-top: 24px;
}
.platform-landing .pl-copy .pl-hint {
  margin-top: 12px;
  font-size: 15px;
  font-weight: 400;
  color: var(--pl-g4);
}

.platform-landing .pl-orbit {
  position: relative;
  flex: none;
  width: 1020px;
  height: 571px;
  z-index: 3;
}
.platform-landing .pl-orbit svg {
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
}

.platform-landing .pl-today {
  position: relative;
  flex: none;
  /* At least the designed width, but grows with its contents (e.g. a wider fallback font) rather than overflowing. */
  width: max-content;
  min-width: 888px;
  max-width: 100%;
  margin-top: 45px;
  height: 112px;
  z-index: 5;
  white-space: nowrap;
  border: 1.5px solid var(--pl-g3);
  border-radius: 18px;
  display: flex;
  align-items: center;
  gap: 22px;
  padding: 0 22px 0 28px;
}
.platform-landing .pl-today .pl-eyebrow {
  flex: none;
  color: var(--pl-g4);
  font-size: 13px;
  line-height: 1.45;
}
.platform-landing .pl-count {
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-size: 17px;
  font-weight: 400;
}
.platform-landing .pl-count b {
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
}
/* Sized for a heading rather than a figure, in both the orbit and list views. */
.platform-landing .pl-count.pl-count-clear b,
.platform-landing .pl-list .pl-count.pl-count-clear b {
  font-size: 24px;
}
.platform-landing .pl-count-clear i {
  transform: translateY(-1px);
}
.platform-landing .pl-count i {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  display: inline-block;
  transform: translateY(-4px);
}
.platform-landing .pl-go {
  flex: none;
  margin-left: auto;
  background: #fff;
  color: var(--pl-navy);
  border: 0;
  border-radius: 999px;
  padding: 14px 20px;
  font:
    700 17px 'Adobe Clean',
    system-ui,
    sans-serif;
  text-decoration: none;
  cursor: pointer;
}

/* ---- the list: for phones, and for anyone who prefers it. Sized in real pixels, not design units. ---- */
.platform-landing .pl-list {
  position: absolute;
  inset: 0;
  overflow-y: auto;
}
.platform-landing .pl-list-content {
  max-width: 720px;
  margin: 0 auto;
  padding: 20px 16px 40px;
}
.platform-landing .pl-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
}
.platform-landing .pl-list-header .pl-brand {
  gap: 14px;
}
.platform-landing .pl-list-header .pl-eyebrow {
  font-size: 12px;
}
.platform-landing .pl-list-header img {
  height: 40px;
  display: block;
  margin-left: -6px;
}
.platform-landing .pl-list h1 {
  font-size: 34px;
  margin: 8px 0 14px;
}
.platform-landing .pl-list-today {
  margin-top: 24px;
  border: 1.5px solid var(--pl-g3);
  border-radius: 18px;
  padding: 16px 18px;
}
.platform-landing .pl-list-today .pl-eyebrow {
  color: var(--pl-g4);
  font-size: 12px;
}
.platform-landing .pl-list-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 22px;
  margin-top: 10px;
}
.platform-landing .pl-list .pl-count b {
  font-size: 30px;
}
.platform-landing .pl-list .pl-key {
  flex-wrap: wrap;
  gap: 8px 18px;
  margin: 24px 0 12px;
  font-size: 14px;
}
.platform-landing .pl-list-tools {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.platform-landing .pl-list-tool {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  color: var(--pl-white);
  text-decoration: none;
}
.platform-landing .pl-list-tool:has(.pl-list-tool-link:hover),
.platform-landing .pl-list-tool:has(.pl-list-tool-link:focus-visible) {
  background: rgba(255, 255, 255, 0.12);
}
.platform-landing .pl-list-tool:has(.pl-list-tool-link:focus-visible) {
  outline: 2px solid rgba(255, 255, 255, 0.6);
}
.platform-landing .pl-list-tool-link {
  color: inherit;
  text-decoration: none;
  outline: none;
}
.platform-landing .pl-list-tool-link::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 14px;
}
/* Navy behind the icon, as on the orbit: some icons have navy knockouts drawn into them. */
.platform-landing .pl-list-tool-locked {
  opacity: 0.55;
}
.platform-landing .pl-list-tool-locked .pl-list-icon img {
  filter: grayscale(1);
}
.platform-landing .pl-list-icon {
  position: relative;
  flex: none;
  width: 56px;
  height: 56px;
  padding: 7px;
  border-radius: 12px;
  background: var(--pl-navy);
}
.platform-landing .pl-list-icon img {
  display: block;
  width: 100%;
  height: 100%;
}
/* The status dot sits on the icon's corner, like the dots and badges on the orbit. */
.platform-landing .pl-list-dot {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 14px;
  height: 14px;
  border: 2.5px solid var(--pl-navy);
  border-radius: 50%;
}
/* "Needs you" carries its count. */
.platform-landing .pl-list-dot-count {
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  color: var(--pl-navy);
  font-size: 14px;
  font-weight: 700;
}
.platform-landing .pl-list-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.platform-landing .pl-list-body b {
  font-size: 18px;
  font-weight: 700;
}
.platform-landing .pl-list-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
}
.platform-landing a.pl-list-item {
  position: relative;
  z-index: 1;
  align-self: flex-start;
  color: var(--pl-link);
  text-decoration: underline;
  text-underline-offset: 3px;
}
.platform-landing a.pl-list-item:hover,
.platform-landing a.pl-list-item:focus-visible {
  color: var(--pl-white);
}
.platform-landing .pl-list-item i {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  transform: translateY(-1px);
}
.platform-landing .pl-list-arrow {
  flex: none;
  align-self: center;
  font-size: 20px;
}

/* ---- the storyline: planet rises and the worker plants the flag, while the orbit assembles alongside. The orbit is done in
   about 1.25s so the page works as a quick app switcher. ---- */
.platform-landing .play #planet {
  animation: pl-rise 0.9s cubic-bezier(0.2, 0.8, 0.2, 1) 0.05s both;
}
.platform-landing .play #man {
  animation: pl-up 0.6s ease-out 0.55s both;
}
.platform-landing .play #flagcloth {
  animation: pl-unfurl 0.55s cubic-bezier(0.3, 1.4, 0.5, 1) 1.75s both;
}
.platform-landing #flagcloth {
  transform-box: fill-box;
  transform-origin: 0 50%;
}
/* The plant. The hand, pole and flag start 30px up (58 in the worker's units) and drive down into the ground, while the
   sleeve shrinks back from stretched by exactly that much (its height is 185 units), so the hand stays on the arm. */
.platform-landing .play .pl-worker .worker-arm-carried {
  animation: pl-drive-worker 0.34s cubic-bezier(0.55, 0, 1, 0.45) 1.28s both;
}
.platform-landing .play .pl-worker .worker-arm-sleeve {
  animation: pl-reach 0.34s cubic-bezier(0.55, 0, 1, 0.45) 1.28s both;
}
.platform-landing .pl-worker .worker-arm-sleeve {
  transform-box: fill-box;
  transform-origin: 50% 100%;
}
.platform-landing .play .pl-plant {
  animation: pl-drive 0.34s cubic-bezier(0.55, 0, 1, 0.45) 1.28s both;
}
.platform-landing .play .pl-hole {
  animation: pl-fade 0.15s ease-out 1.6s both;
}
/* Forwards, not both: invisible until the pole lands. */
.platform-landing .play .dust circle {
  animation: pl-dust 0.7s ease-out 1.6s forwards;
}
.platform-landing .dust circle {
  opacity: 0;
  transform-box: fill-box;
  transform-origin: center;
}
.platform-landing .play .pl-copy > * {
  animation: pl-up 0.6s ease-out both;
}
.platform-landing .play .pl-copy > :nth-child(2) {
  animation-delay: 0.12s;
}
.platform-landing .play .pl-copy > :nth-child(3) {
  animation-delay: 0.24s;
}
.platform-landing .play .pl-copy > :nth-child(4) {
  animation-delay: 0.36s;
}
.platform-landing .play .pl-copy > :nth-child(5) {
  animation-delay: 0.44s;
}
.platform-landing .play #Orbit_rings {
  animation: pl-rings 0.6s ease-out 0.05s both;
}
.platform-landing #Orbit_rings {
  transform-box: fill-box;
  transform-origin: center;
}
.platform-landing .play #Stars {
  animation: pl-fade 0.5s ease-out 0.35s both;
}
.platform-landing .play .pf-node {
  animation: pl-pop 0.4s ease-out both;
  animation-delay: calc(0.6s + var(--i, 4) * 0.03s);
}
.platform-landing .pf-node {
  transform-box: fill-box;
  transform-origin: center;
}
.platform-landing .play .pf-tool {
  animation: pl-pop 0.4s cubic-bezier(0.3, 1.5, 0.5, 1) both;
  animation-delay: calc(0.2s + var(--i) * 0.05s);
}
.platform-landing .pf-tool {
  transform-box: fill-box;
  transform-origin: center;
}
.platform-landing .play .pf-badge {
  animation: pl-pop 0.4s cubic-bezier(0.3, 1.6, 0.5, 1) both;
  animation-delay: calc(0.75s + var(--i) * 0.03s);
}
.platform-landing .pf-badge {
  transform-box: fill-box;
  transform-origin: center;
}
.platform-landing .play .pl-today {
  animation: pl-up 0.5s ease-out 0.7s both;
}

@media (prefers-reduced-motion: reduce) {
  .platform-landing *,
  .platform-landing *::before,
  .platform-landing *::after {
    animation: none !important;
    transition: none !important;
  }
}

@keyframes pl-pulse {
  0% {
    opacity: 0.7;
    transform: scale(1);
  }
  70%,
  100% {
    opacity: 0;
    transform: scale(1.9);
  }
}
@keyframes pl-breathe {
  50% {
    opacity: 0.3;
  }
}
@keyframes pl-twinkle {
  50% {
    opacity: 0.1;
  }
}
@keyframes pl-rise {
  from {
    transform: translateY(260px);
  }
}
@keyframes pl-up {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
}
@keyframes pl-fade {
  from {
    opacity: 0;
  }
}
@keyframes pl-unfurl {
  from {
    transform: scaleX(0.05);
  }
}
@keyframes pl-drive {
  from {
    transform: translateY(-30px);
  }
}
@keyframes pl-drive-worker {
  from {
    transform: translateY(-58px);
  }
}
@keyframes pl-reach {
  from {
    transform: scaleY(1.314);
  }
}
@keyframes pl-rings {
  from {
    opacity: 0;
    transform: scale(0.82);
  }
}
@keyframes pl-pop {
  from {
    opacity: 0;
    transform: scale(0.3);
  }
}
@keyframes pl-dust {
  0% {
    opacity: 0.9;
    transform: scale(0.2);
  }
  100% {
    opacity: 0;
    transform: translate(var(--dx), -10px) scale(1.4);
  }
}
</style>
